import { Card, Stack, Typography } from '@mui/material'
import { ApexOptions } from 'apexcharts'
import { FC } from 'react'
import Chart from 'react-apexcharts'

type DataItem = {
  label: number
  eventCount: number
}

type Props = {
  title: string
  xTitle: string
  data: DataItem[]
  color?: string
}

export const RequestEventDashboardGraph: FC<Props> = ({
  title,
  data,
  xTitle,
  color = '#4CAF50',
}) => {
  const converted_data: ApexAxisChartSeries = [
    {
      name: '件数',
      type: 'bar',
      data: data.map((item) => item.eventCount),
    },
  ]

  const [max, tickAmount] = (() => {
    const max = Math.max.apply(Math, [
      10,
      ...data.map((item) => item.eventCount),
    ])
    const maxBase = Math.pow(10, Math.floor(Math.log10(max)))
    const bigDigit = Math.ceil(max / maxBase)
    return [
      maxBase * bigDigit,
      bigDigit === 1
        ? 5
        : bigDigit <= 4
        ? 2 * bigDigit
        : bigDigit <= 8
        ? bigDigit
        : 6,
    ]
  })()

  const options: ApexOptions = {
    dataLabels: {
      enabled: false,
    },
    colors: [color],
    chart: {
      id: title,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      fontFamily: 'Helvetica, Arial, sans-serif',
      width: '100%',
      height: '100%',
      parentHeightOffset: 0,
      background: 'transparent',
    },
    responsive: [
      {
        breakpoint: 1000,
        options: {
          chart: {
            width: '98%',
          },
        },
      },
    ],
    xaxis: {
      categories: data.map((item) => item.label),
      labels: {
        hideOverlappingLabels: true,
        formatter(value) {
          return value !== undefined ? value.toString() : ''
        },
        rotate: 0,
        style: {
          fontSize: '11px',
          fontWeight: 500,
        },
        offsetY: 5,
      },
      title: {
        text: xTitle,
        style: {
          fontSize: '1em',
          fontWeight: 600,
        },
        offsetY: 10,
      },
    },
    yaxis: {
      labels: {
        formatter(val) {
          return val.toFixed(0)
        },
        style: {
          fontSize: '12px',
        },
        offsetX: 0,
      },
      max,
      tickAmount,
      title: {
        text: '件数',
        style: {
          fontSize: '0.9em',
          fontWeight: 600,
        },
        offsetX: 0,
        offsetY: 0,
        rotate: -90,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 2,
        distributed: false,
        dataLabels: {
          position: 'top',
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val) {
          return val + ' 件'
        },
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.2,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 0.85,
        opacityTo: 0.85,
      },
    },
  }

  return (
    <Stack
      direction="column"
      sx={{
        height: '100%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'white',
        width: '100%',
        boxSizing: 'border-box',
        maxWidth: '100%',
      }}
    >
      <Typography
        variant="h6"
        align="center"
        sx={{
          py: 2,
          px: 2,
          fontWeight: 600,
          color: '#424242',
          fontSize: '1.1rem',
        }}
      >
        {title}
      </Typography>
      <Card
        sx={{
          flexGrow: 1,
          px: 3,
          py: 2,
          boxShadow: 'none',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Chart
            type="bar"
            options={options}
            series={converted_data}
            height="100%"
            width="100%"
          />
        </div>
      </Card>
    </Stack>
  )
}
